import { useState, type FormEvent, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ApiError,
  createAsset,
  deleteAsset,
  fetchAssetTasks,
  fetchAssets,
  fetchAlertEvents,
  fetchAlertRules,
  fetchTelemetryDaily,
  fetchTelemetryLatest,
  issueAgentToken,
  listAgentTokens,
  openRemoteSession,
  revokeAgentToken,
  rotateAgentToken,
} from '../api/client';
import type {
  AgentTokenView,
  AlertEventResponse,
  AlertRuleResponse,
  AssetResponse,
  FieldTask,
  IssuedAgentToken,
  TelemetryDailyPoint,
  TelemetryLatestMetric,
} from '../api/types';
import { config } from '../config';
import {
  type BadgeAlat,
  badgeKesehatanAlat,
  kejadianPerAlat,
  severitasAturan,
} from '../lib/asset-health-badge';
import {
  HEALTH_LABEL,
  dataBasi,
  formatMetrik,
  jumlahAlertTerbuka,
  seriRataHarian,
  sparklinePoints,
  statusKesehatan,
  waktuPembacaanTerbaru,
} from '../lib/telemetry-health';
import type { AuthState } from '../api/request';
import { useSession } from '../app/WorkforceContext';
import { useAsync } from '../app/useAsync';
import {
  namaAlatProblem,
  normalizeRustdeskId,
  provinsiTersedia,
  rustdeskDeepLink,
  rustdeskIdProblem,
  saringAlatProvinsi,
} from '../lib/asset-form';
import { type AgenOS, perintahPasang, statusAgen, tokenAktif } from '../lib/agent-enrollment';
import { ASET_BINER, namaSkrip, skripPasang } from '../lib/agent-install-script';
import { umurSingkat } from '../lib/last-seen';
import { EmptyState, ErrorCard, LoadingState } from '../states';

const STATUS_LABEL: Record<AssetResponse['status'], string> = {
  ACTIVE: 'Aktif',
  INACTIVE: 'Nonaktif',
  RETIRED: 'Pensiun',
};

/**
 * Badge kesehatan di kartu alat (ADR-0055 Fase 3). Warna memakai pasangan latar/teks status yang
 * sama dengan sisa app (lulus WCAG, lihat styles.css); `dot` + label teks → warna tak pernah jadi
 * satu-satunya penanda. null = tak ada kejadian terbuka → tak ditampilkan.
 */
const BADGE_STYLE: Record<BadgeAlat['tone'], { bg: string; fg: string; dot: string }> = {
  warn: { bg: 'var(--warn-bg)', fg: 'var(--warn)', dot: 'var(--warn)' },
  crit: { bg: 'var(--danger-bg)', fg: 'var(--danger)', dot: 'var(--danger)' },
  off: { bg: 'var(--line)', fg: 'var(--ink)', dot: 'var(--ink)' },
};

function HealthBadge({ badge }: { readonly badge: BadgeAlat | null }): ReactNode {
  if (!badge) return null;
  const s = BADGE_STYLE[badge.tone];
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '0.35rem',
        padding: '0.2rem 0.55rem',
        borderRadius: 'var(--r-pill)',
        fontSize: '0.75rem',
        fontWeight: 700,
        background: s.bg,
        color: s.fg,
        whiteSpace: 'nowrap',
      }}
    >
      <span
        aria-hidden="true"
        style={{
          width: '0.5rem',
          height: '0.5rem',
          borderRadius: '999px',
          background: s.dot,
          display: 'inline-block',
        }}
      />
      {badge.label}
    </span>
  );
}

function fmtTanggal(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleDateString('id-ID');
}

const TASK_STATUS_LABEL: Record<string, string> = {
  draft: 'Draf',
  assigned: 'Ditugaskan',
  accepted: 'Diterima',
  started: 'Dikerjakan',
  arrived: 'Di lokasi',
  completed: 'Selesai',
  failed: 'Gagal',
  cancelled: 'Dibatalkan',
};

/**
 * Riwayat servis per alat (ADR-0054 §7, Fase 2). Lazy — hanya memuat saat dibuka, agar membuka
 * halaman Alat tidak menarik riwayat semua alat sekaligus. Tiket dibaca lewat filter externalRef
 * di server; visibilitasnya tetap sesuai peran (teknisi hanya cakupannya).
 */
function ServiceHistory({
  auth,
  assetId,
}: {
  readonly auth: AuthState;
  readonly assetId: string;
}): ReactNode {
  const { state, reload } = useAsync<readonly FieldTask[]>(
    () => fetchAssetTasks(auth, assetId),
    [auth, assetId],
  );
  if (state.status === 'loading') return <p className="muted small">Memuat riwayat servis…</p>;
  if (state.status === 'error') return <ErrorCard error={state.error} onRetry={reload} />;
  if (state.data.length === 0)
    return <p className="muted small">Belum ada tiket servis untuk alat ini.</p>;
  return (
    <ul className="muted small">
      {state.data.map((t) => (
        <li key={t.id}>
          {t.taskNumber} · {t.title} · {TASK_STATUS_LABEL[t.status] ?? t.status} ·{' '}
          {fmtTanggal(t.createdAt)}
        </li>
      ))}
    </ul>
  );
}

const METRIC_LABEL: Record<string, string> = {
  status: 'Status',
  uptime_seconds: 'Uptime',
  cpu_percent: 'CPU',
  memory_percent: 'Memori',
  disk_percent: 'Disk',
  temperature_c: 'Suhu',
  error_code: 'Kode error',
};

interface TelemetrySnapshot {
  readonly latest: readonly TelemetryLatestMetric[];
  readonly alerts: readonly AlertEventResponse[];
  readonly cpuDaily: readonly TelemetryDailyPoint[];
}

/**
 * Kesehatan alat per kartu (ADR-0055 Fase 3 slice 5b). Lazy seperti riwayat servis: memuat status
 * terkini + alert terbuka + tren CPU 30 hari HANYA saat dibuka. Kesehatan diturunkan dari metrik
 * `status` yang dilaporkan agen (ambang numerik milik server), sparkline dari agregat harian.
 */
function TelemetryPanel({
  auth,
  assetId,
}: {
  readonly auth: AuthState;
  readonly assetId: string;
}): ReactNode {
  const { state, reload } = useAsync<TelemetrySnapshot>(async () => {
    const [latest, alerts, cpuDaily] = await Promise.all([
      fetchTelemetryLatest(auth, assetId),
      fetchAlertEvents(auth, { assetId, open: true }),
      fetchTelemetryDaily(auth, assetId, 'cpu_percent', 30),
    ]);
    return { latest, alerts, cpuDaily };
  }, [auth, assetId]);

  if (state.status === 'loading') return <p className="muted small">Memuat kesehatan alat…</p>;
  if (state.status === 'error') return <ErrorCard error={state.error} onRetry={reload} />;

  const { latest, alerts, cpuDaily } = state.data;
  if (latest.length === 0)
    return (
      <p className="muted small">Belum ada telemetri — agen di PC alat belum mengirim data.</p>
    );

  const health = statusKesehatan(latest);
  const openAlerts = jumlahAlertTerbuka(alerts);
  const spark = sparklinePoints(seriRataHarian(cpuDaily), 160, 32);
  const terbaruMs = waktuPembacaanTerbaru(latest);
  const basi = dataBasi(latest, new Date());
  const menitLalu = terbaruMs === null ? null : Math.round((Date.now() - terbaruMs) / 60000);

  return (
    <div className="muted small">
      {basi ? (
        <p role="status" style={{ color: 'var(--danger, #c0392b)', fontWeight: 600 }}>
          ⚠ Agen tampak offline — pembacaan terakhir
          {menitLalu === null ? '' : ` ${menitLalu} menit`} lalu. Status di bawah mungkin usang.
        </p>
      ) : null}
      <p>
        Kesehatan: <strong>{basi ? 'Offline?' : HEALTH_LABEL[health]}</strong>
        {' · '}
        Alert terbuka: <strong>{openAlerts}</strong>
      </p>
      <ul>
        {latest.map((m) => (
          <li key={m.metricKey}>
            {METRIC_LABEL[m.metricKey] ?? m.metricKey}:{' '}
            {formatMetrik(m.metricKey, m.metricValue, m.metricText)}
          </li>
        ))}
      </ul>
      {spark ? (
        <svg width={160} height={32} role="img" aria-label="Tren CPU 30 hari">
          <polyline points={spark} fill="none" stroke="currentColor" strokeWidth={1.5} />
        </svg>
      ) : (
        <p>Belum ada tren CPU harian.</p>
      )}
    </div>
  );
}

const STATUS_AGEN_TONE: Record<ReturnType<typeof statusAgen>, { label: string; warn?: boolean }> = {
  'tidak-ada': { label: 'Belum dipantau' },
  'belum-terlihat': { label: 'Menunggu agen' },
  aktif: { label: 'Agen aktif' },
  offline: { label: 'Agen offline', warn: true },
};

/**
 * Panel "Agen telemetri" per kartu alat (ADR-0055 Fase 3) — enrollment agen 1-klik.
 *
 * Backend-nya sudah ada (slice 1): daftar/terbit/rotasi/cabut token, dijaga `workforce.asset.update`
 * (ditampilkan HANYA bila punya izin itu — pola `canDelete`). Nilai token mentah hanya kembali
 * SEKALI pada terbit/rotasi; panel menampilkannya satu kali di `issued` lalu tak pernah lagi.
 * Lazy seperti riwayat & kesehatan: memuat daftar token hanya saat dibuka.
 */
function AgentPanel({
  auth,
  assetId,
}: {
  readonly auth: AuthState;
  readonly assetId: string;
}): ReactNode {
  const { state, reload } = useAsync<readonly AgentTokenView[]>(
    () => listAgentTokens(auth, assetId),
    [auth, assetId],
  );
  const [issued, setIssued] = useState<IssuedAgentToken | null>(null);
  const [os, setOs] = useState<AgenOS>('windows');
  const [busy, setBusy] = useState<null | 'issue' | 'rotate' | 'revoke'>(null);
  const [konfirmCabut, setKonfirmCabut] = useState(false);
  const [disalin, setDisalin] = useState<null | 'token' | 'perintah'>(null);
  const [galat, setGalat] = useState<ApiError | null>(null);

  if (state.status === 'loading') return <p className="muted small">Memuat status agen…</p>;
  if (state.status === 'error') return <ErrorCard error={state.error} onRetry={reload} />;

  const tokens = state.data;
  const status = statusAgen(tokens, new Date());
  const aktif = tokenAktif(tokens);
  const tone = STATUS_AGEN_TONE[status];

  async function jalankan(
    aksi: 'issue' | 'rotate' | 'revoke',
    fn: () => Promise<IssuedAgentToken | { ok: true }>,
  ): Promise<void> {
    setBusy(aksi);
    setGalat(null);
    try {
      const hasil = await fn();
      // Terbit & rotasi mengembalikan token mentah SEKALI; cabut tidak.
      if ('token' in hasil) setIssued(hasil);
      setKonfirmCabut(false);
      await reload();
    } catch (e) {
      setGalat(e instanceof ApiError ? e : new ApiError(0, 'ERROR', 'Terjadi kesalahan.', null));
    } finally {
      setBusy(null);
    }
  }

  async function salin(apa: 'token' | 'perintah', teks: string): Promise<void> {
    try {
      await navigator.clipboard.writeText(teks);
      setDisalin(apa);
      setTimeout(() => setDisalin(null), 1500);
    } catch {
      // Clipboard diblokir (mis. konteks non-aman) — operator masih bisa menyalin manual.
    }
  }

  /**
   * Unduh skrip pasang dengan token tertanam (satu-klik di PC alat). Blob dibuat di browser dari
   * teks yang dihasilkan lib murni — tak ada permintaan jaringan, tak ada endpoint baru.
   */
  function unduhSkrip(tokenMentah: string): void {
    const teks = skripPasang(config.apiBaseUrl, tokenMentah, os, config.agentDistBaseUrl);
    const blob = new Blob([teks], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = namaSkrip(os);
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  }

  // ── Token sekali-tampil (setelah terbit/rotasi) ──────────────────────────
  if (issued) {
    const cmd = perintahPasang(config.apiBaseUrl, issued.token, os);
    const tab = (nilai: AgenOS, label: string): ReactNode => (
      <button
        type="button"
        className="chip"
        aria-pressed={os === nilai}
        onClick={() => setOs(nilai)}
        style={os === nilai ? { background: 'var(--ink)', color: 'var(--ink-invert)' } : undefined}
      >
        {label}
      </button>
    );
    return (
      <div className="small" style={{ marginTop: '0.5rem' }}>
        <p role="status" className="warning">
          ⚠ Token hanya ditampilkan sekali. Salin sekarang — tidak bisa dilihat lagi.
        </p>
        <div
          style={{
            display: 'flex',
            gap: '0.5rem',
            alignItems: 'center',
            background: 'var(--sunken)',
            borderRadius: 'var(--r-field)',
            padding: '0.6rem 0.7rem',
          }}
        >
          <code
            style={{
              fontFamily: 'ui-monospace, Menlo, monospace',
              wordBreak: 'break-all',
              flex: 1,
            }}
          >
            {issued.token}
          </code>
          <button
            type="button"
            className="chip"
            onClick={() => void salin('token', issued.token)}
            style={{ flex: '0 0 auto' }}
          >
            {disalin === 'token' ? 'Tersalin' : 'Salin'}
          </button>
        </div>

        <p className="muted" style={{ margin: '0.75rem 0 0.4rem' }}>
          Jalankan di PC alat (set dua env var lalu jalankan biner):
        </p>
        <div className="row" style={{ marginBottom: '0.4rem' }}>
          {tab('windows', 'Windows')}
          {tab('linux', 'Linux')}
        </div>
        <pre
          style={{
            background: 'var(--ink)',
            color: '#e9e7e2',
            borderRadius: 'var(--r-field)',
            padding: '0.7rem 0.8rem',
            margin: 0,
            fontFamily: 'ui-monospace, Menlo, monospace',
            fontSize: '0.75rem',
            lineHeight: 1.6,
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-all',
          }}
        >
          {cmd}
        </pre>
        <button type="button" onClick={() => void salin('perintah', cmd)}>
          {disalin === 'perintah' ? 'Perintah tersalin' : 'Salin perintah'}
        </button>
        <button type="button" className="button-primary" onClick={() => unduhSkrip(issued.token)}>
          Unduh skrip pasang ({os === 'windows' ? 'Windows' : 'Linux'})
        </button>
        {config.agentDistBaseUrl ? (
          <>
            <a
              className="button-link"
              href={`${config.agentDistBaseUrl}/${ASET_BINER[os]}`}
              target="_blank"
              rel="noopener noreferrer"
            >
              Unduh biner agen ({os === 'windows' ? 'Windows' : 'Linux'})
            </a>
            <p className="muted" style={{ marginTop: '0.6rem' }}>
              Cara tercepat: unduh skrip (token sudah tertanam), jalankan di PC alat — skrip
              mengunduh biner sendiri lalu memasang agen sebagai layanan. Agen mengirim data &lt; 1
              menit setelah jalan.
            </p>
          </>
        ) : (
          <p className="muted" style={{ marginTop: '0.6rem' }}>
            Cara tercepat: unduh skrip (token sudah tertanam), taruh di folder yang sama dengan
            biner agen di PC alat, lalu jalankan — agen langsung terpasang sebagai layanan. Biner
            di-build dari repo (apps/workforce-agent); langkah lengkap ada di runbook enrollment.
            Agen mengirim data &lt; 1 menit setelah jalan.
          </p>
        )}
        <button type="button" className="button-link" onClick={() => setIssued(null)}>
          Selesai
        </button>
      </div>
    );
  }

  // ── Status agen + aksi ───────────────────────────────────────────────────
  return (
    <div className="small" style={{ marginTop: '0.5rem' }}>
      {galat ? <ErrorCard error={galat} onRetry={() => setGalat(null)} /> : null}
      <p className={tone.warn ? 'warn-text' : undefined}>
        {status === 'aktif' && aktif?.lastSeenAt
          ? `Agen aktif · terlihat ${umurSingkat(aktif.lastSeenAt, new Date())}`
          : status === 'offline' && aktif?.lastSeenAt
            ? `Agen offline · terakhir ${umurSingkat(aktif.lastSeenAt, new Date())}`
            : status === 'belum-terlihat'
              ? 'Token terbit — menunggu agen mengirim data pertama.'
              : 'Belum ada agen telemetri di PC alat ini.'}
      </p>
      {status === 'tidak-ada' ? (
        <button
          type="button"
          disabled={busy !== null}
          onClick={() => void jalankan('issue', () => issueAgentToken(auth, assetId))}
        >
          {busy === 'issue' ? 'Menerbitkan…' : 'Terbitkan token & pasang'}
        </button>
      ) : (
        <div className="row">
          <button
            type="button"
            className="chip"
            disabled={busy !== null}
            onClick={() => void jalankan('rotate', () => rotateAgentToken(auth, assetId))}
          >
            {busy === 'rotate' ? 'Merotasi…' : 'Rotasi token'}
          </button>
          {konfirmCabut ? (
            <>
              <button
                type="button"
                className="chip"
                disabled={busy !== null}
                onClick={() => void jalankan('revoke', () => revokeAgentToken(auth, assetId))}
              >
                {busy === 'revoke' ? 'Mencabut…' : 'Yakin cabut'}
              </button>
              <button type="button" className="chip" onClick={() => setKonfirmCabut(false)}>
                Batal
              </button>
            </>
          ) : (
            <button type="button" className="chip" onClick={() => setKonfirmCabut(true)}>
              Cabut
            </button>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * Daftar Alat (installed base) + remote per alat — ADR-0054 Fase 1.
 *
 * Remote terikat ke SATU komputer alat (§5): membuka sesi mencatat audit lalu menyambung ke
 * `rustdesk://` komputer ITU saja. Menghapus alat (soft-delete) hanya tampil bagi yang punya
 * izin `workforce.asset.delete` — menyembunyikan tombol yang pasti ditolak server.
 */
export function AssetsPage(): ReactNode {
  const session = useSession();
  const { auth, me, onSignOut } = session;
  const canDelete = me.permissions.includes('workforce.asset.delete');
  // ADR-0054 Fase 2 — "Buat tiket servis" membawa alat ke form tugas lewat `?assetId=`.
  // Disembunyikan tanpa izin buat-tugas (pola sama dengan `canDelete`): menyembunyikan tombol
  // yang pasti ditolak server, bukan kontrol keamanan.
  const canCreateTask = me.permissions.includes('workforce.task.create');
  // ADR-0055 Fase 3 — manajemen token agen dijaga `workforce.asset.update` (peran yang sama yang
  // menyunting alat). Disembunyikan tanpa izin itu (pola `canDelete`): GET daftar token pun ditolak
  // server tanpa izin ini, jadi menampilkan tombolnya hanya akan memunculkan error.
  const canManageAgent = me.permissions.includes('workforce.asset.update');
  // ADR-0055 Fase 3 — badge kesehatan di daftar diturunkan dari kejadian alert TERBUKA (butuh
  // `telemetry.read`) + severity aturan (butuh `asset.update`). Hanya menandai alat yang punya
  // kejadian: absennya alert BUKAN klaim "sehat". Lihat lib/asset-health-badge.
  const canReadTelemetry = me.permissions.includes('workforce.telemetry.read');
  const navigate = useNavigate();

  const { state, reload } = useAsync(() => fetchAssets(auth), [auth]);

  // Overlay kesehatan — satu panggilan kejadian terbuka se-armada (+ aturan bila boleh). Gagal/izin
  // kurang tidak boleh menjatuhkan daftar alat, jadi di-swallow jadi badge kosong.
  const { state: healthState } = useAsync<{
    readonly events: readonly AlertEventResponse[];
    readonly rules: readonly AlertRuleResponse[];
  }>(async () => {
    if (!canReadTelemetry) return { events: [], rules: [] };
    const events = await fetchAlertEvents(auth, { open: true });
    const rules = canManageAgent ? await fetchAlertRules(auth) : [];
    return { events, rules };
  }, [auth, canReadTelemetry, canManageAgent]);

  const badgePerAlat: ReadonlyMap<string, BadgeAlat> = (() => {
    if (healthState.status !== 'ready') return new Map();
    const sev = severitasAturan(healthState.data.rules);
    const perAlat = kejadianPerAlat(healthState.data.events);
    const peta = new Map<string, BadgeAlat>();
    for (const [assetId, evs] of perAlat) {
      const b = badgeKesehatanAlat(evs, sev);
      if (b) peta.set(assetId, b);
    }
    return peta;
  })();

  const [nama, setNama] = useState('');
  const [rustdeskId, setRustdeskId] = useState('');
  const [provinsi, setProvinsi] = useState('');
  const [mengirim, setMengirim] = useState(false);
  const [galat, setGalat] = useState<string | null>(null);
  const [galatApi, setGalatApi] = useState<ApiError | null>(null);
  const [konfirmHapus, setKonfirmHapus] = useState<string | null>(null);
  const [bukaId, setBukaId] = useState<string | null>(null);
  const [riwayatId, setRiwayatId] = useState<string | null>(null);
  const [kesehatanId, setKesehatanId] = useState<string | null>(null);
  const [agenId, setAgenId] = useState<string | null>(null);
  // ADR-0054 Fase 2 — filter wilayah (keputusan Owner: pakai field provinsi di alat). '' = semua.
  const [filterProvinsi, setFilterProvinsi] = useState('');

  async function tambah(e: FormEvent): Promise<void> {
    e.preventDefault();
    const masalah = namaAlatProblem(nama) ?? rustdeskIdProblem(rustdeskId);
    if (masalah) {
      setGalat(masalah);
      return;
    }
    setGalat(null);
    setGalatApi(null);
    setMengirim(true);
    try {
      await createAsset(auth, {
        name: nama.trim(),
        rustdeskId: normalizeRustdeskId(rustdeskId),
        provinsi: provinsi.trim() || undefined,
      });
      setNama('');
      setRustdeskId('');
      setProvinsi('');
      reload();
    } catch (err) {
      if (err instanceof ApiError) setGalatApi(err);
      else setGalat('Gagal menyimpan alat.');
    } finally {
      setMengirim(false);
    }
  }

  async function bukaRemote(asset: AssetResponse): Promise<void> {
    setGalatApi(null);
    setBukaId(asset.id);
    try {
      const res = await openRemoteSession(auth, { assetId: asset.id });
      window.location.assign(rustdeskDeepLink(res.rustdeskId));
    } catch (err) {
      if (err instanceof ApiError) setGalatApi(err);
      else setGalat('Gagal membuka sesi remote.');
    } finally {
      setBukaId(null);
    }
  }

  async function hapus(asset: AssetResponse): Promise<void> {
    setGalatApi(null);
    try {
      await deleteAsset(auth, asset.id);
      setKonfirmHapus(null);
      reload();
    } catch (err) {
      if (err instanceof ApiError) setGalatApi(err);
      else setGalat('Gagal menghapus alat.');
    }
  }

  // ADR-0054 Fase 2 — opsi & hasil filter wilayah dihitung dari daftar yang sudah diambil.
  const opsiProvinsi = state.status === 'ready' ? provinsiTersedia(state.data) : [];
  const alatTerlihat =
    state.status === 'ready' ? saringAlatProvinsi(state.data, filterProvinsi) : [];

  return (
    <div className="page">
      <header className="page-header">
        <h1>Alat</h1>
        <p className="muted">
          Installed base alat kesehatan faskes. Remote hanya ke komputer alat (RustDesk self-host),
          khusus teknisi/manajer.
        </p>
      </header>

      <section className="card">
        <h2>Daftarkan alat</h2>
        <form onSubmit={tambah}>
          <label>
            Nama alat
            <input
              value={nama}
              onChange={(e) => setNama(e.target.value)}
              placeholder="mis. USG Klinik Sehat A"
              maxLength={160}
            />
          </label>
          <label>
            RustDesk ID (6–16 digit angka)
            <input
              value={rustdeskId}
              onChange={(e) => setRustdeskId(e.target.value)}
              placeholder="mis. 123 456 789"
              inputMode="numeric"
            />
          </label>
          <label>
            Provinsi (opsional)
            <input
              value={provinsi}
              onChange={(e) => setProvinsi(e.target.value)}
              placeholder="mis. Banten"
              maxLength={80}
            />
          </label>
          {galat ? <p className="error small">{galat}</p> : null}
          <button type="submit" disabled={mengirim}>
            {mengirim ? 'Menyimpan…' : 'Tambah alat'}
          </button>
        </form>
      </section>

      {galatApi ? <ErrorCard error={galatApi} onRetry={reload} onSignOut={onSignOut} /> : null}

      {state.status === 'loading' ? <LoadingState label="Memuat alat…" /> : null}
      {state.status === 'error' ? (
        <ErrorCard error={state.error} onRetry={reload} onSignOut={onSignOut} />
      ) : null}
      {state.status === 'ready' && state.data.length === 0 ? (
        <EmptyState
          title="Belum ada alat"
          hint="Daftarkan PC alat (dengan RustDesk ID) agar bisa dibantu dari jarak jauh."
        />
      ) : null}

      {state.status === 'ready' && opsiProvinsi.length > 0 ? (
        <section className="card">
          <label htmlFor="filter-provinsi">Wilayah</label>
          <select
            id="filter-provinsi"
            value={filterProvinsi}
            onChange={(e) => setFilterProvinsi(e.target.value)}
          >
            <option value="">Semua wilayah</option>
            {opsiProvinsi.map((p) => (
              <option key={p} value={p}>
                {p}
              </option>
            ))}
          </select>
        </section>
      ) : null}

      {state.status === 'ready' && state.data.length > 0 && alatTerlihat.length === 0 ? (
        <EmptyState
          title="Tidak ada alat di wilayah ini"
          hint="Pilih wilayah lain, atau kembali ke Semua wilayah."
        />
      ) : null}

      {state.status === 'ready'
        ? alatTerlihat.map((asset) => (
            <section className="card" key={asset.id}>
              <div className="row">
                <div>
                  <span
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem',
                      flexWrap: 'wrap',
                    }}
                  >
                    <h3 style={{ display: 'inline' }}>{asset.name}</h3>
                    <HealthBadge badge={badgePerAlat.get(asset.id) ?? null} />
                  </span>
                  <p className="muted small">
                    {asset.rustdeskId} · {STATUS_LABEL[asset.status]}
                    {asset.provinsi ? ` · ${asset.provinsi}` : ''}
                  </p>
                  <p className="muted small">Terdaftar {fmtTanggal(asset.createdAt)}</p>
                </div>
              </div>
              <div className="row">
                <button
                  type="button"
                  onClick={() => void bukaRemote(asset)}
                  disabled={bukaId === asset.id || asset.status !== 'ACTIVE'}
                >
                  {bukaId === asset.id ? 'Membuka…' : 'Buka Remote'}
                </button>
                {canDelete && konfirmHapus !== asset.id ? (
                  <button type="button" onClick={() => setKonfirmHapus(asset.id)}>
                    Hapus
                  </button>
                ) : null}
                {canDelete && konfirmHapus === asset.id ? (
                  <>
                    <button type="button" onClick={() => void hapus(asset)}>
                      Yakin hapus
                    </button>
                    <button type="button" onClick={() => setKonfirmHapus(null)}>
                      Batal
                    </button>
                  </>
                ) : null}
                <button
                  type="button"
                  onClick={() => setRiwayatId((cur) => (cur === asset.id ? null : asset.id))}
                >
                  {riwayatId === asset.id ? 'Tutup riwayat' : 'Riwayat servis'}
                </button>
                <button
                  type="button"
                  onClick={() => setKesehatanId((cur) => (cur === asset.id ? null : asset.id))}
                >
                  {kesehatanId === asset.id ? 'Tutup kesehatan' : 'Kesehatan alat'}
                </button>
                {canManageAgent ? (
                  <button
                    type="button"
                    onClick={() => setAgenId((cur) => (cur === asset.id ? null : asset.id))}
                  >
                    {agenId === asset.id ? 'Tutup agen' : 'Agen telemetri'}
                  </button>
                ) : null}
                {canCreateTask ? (
                  <button type="button" onClick={() => navigate(`/tasks/new?assetId=${asset.id}`)}>
                    Buat tiket servis
                  </button>
                ) : null}
              </div>
              {riwayatId === asset.id ? <ServiceHistory auth={auth} assetId={asset.id} /> : null}
              {kesehatanId === asset.id ? <TelemetryPanel auth={auth} assetId={asset.id} /> : null}
              {canManageAgent && agenId === asset.id ? (
                <AgentPanel auth={auth} assetId={asset.id} />
              ) : null}
            </section>
          ))
        : null}
    </div>
  );
}
